import React, { createContext, useState, useContext, useEffect } from 'react';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    const savedCart = localStorage.getItem('cart');
    return savedCart ? JSON.parse(savedCart) : [];
  });

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  // Get unique identifier for cart item (handles both regular products and variants)
  const getItemKey = (item) => {
    return item.cartItemId || item._id;
  };

  const addToCart = (product) => {
    setCart(prevCart => {
      const itemKey = getItemKey(product);
      const existingItem = prevCart.find(item => getItemKey(item) === itemKey);
      
      if (existingItem) {
        return prevCart.map(item =>
          getItemKey(item) === itemKey
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      
      return [...prevCart, { ...product, quantity: 1 }];
    });
  };

  const removeFromCart = (itemKey) => {
    setCart(prevCart => prevCart.filter(item => getItemKey(item) !== itemKey));
  };

  const updateQuantity = (itemKey, quantity) => {
    if (quantity <= 0) {
      removeFromCart(itemKey);
      return;
    }
    
    setCart(prevCart =>
      prevCart.map(item =>
        getItemKey(item) === itemKey ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setCart([]);
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getCartCount = () => {
    return cart.reduce((count, item) => count + item.quantity, 0);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartTotal,
        getCartCount,
        getItemKey
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
